import cx from 'classnames';
import {useMemo, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useSelector} from 'react-redux';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {PhotoUploadButton} from '~/ViewInvitation/routes/Landing/PhotosModule/PhotoUploadButton';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {selectIsPremium} from '~/ViewInvitation/selectors/eventDetails';
import {isHost} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {AdaptiveModal} from '~library/organisms/AdaptiveModal';
import {CameraIcon} from '~library/svg/v2/CameraIcon';
import {EditLineIcon} from '~library/svg/v2/EditLineIcon';
import {PlusIcon} from '~library/svg/v2/PlusIcon';

import 'react-loading-skeleton/dist/skeleton.css';
import './PhotoGallery.sass';

const EmptyState = ({handlePost, fileUploadRef, showComingSoonState}) => {
  const eventTitle = useSelector((state) => state.eventDetails?.title);

  return showComingSoonState
    ? <div className="photo-gallery__coming-soon-state__container">
        <CameraIcon />
        <Typography variant="paragraph1" semibold>
          Coming soon!
        </Typography>
        <Typography variant="paragraph3">
          You'll be able to add photos once the event starts.
        </Typography>
      </div>
    : <div className="photo-gallery__empty-state__container">
        <Typography variant="header5">{eventTitle}</Typography>
        <Typography variant="paragraph2">No photos yet - be the first to share!</Typography>
        <PhotoUploadButton largeButton handlePost={handlePost} fileUploadRef={fileUploadRef}>
          Add photos
        </PhotoUploadButton>
      </div>;
};

const loadingSkeletons = (count) =>
  new Array(count).fill(null).map((_, i) => (
    <Skeleton
      // biome-ignore lint/suspicious/noArrayIndexKey: allow index here, as they're all identical
      key={i}
      containerClassName="photo-gallery__photo-button"
      className="photo-gallery__photo"
    />
  ));

const PhotoGalleryTile = ({i, image, url, viewPhoto, getThumbnailUrl, isHostModule}) => {
  const [isLoading, setIsLoading] = useState(true);
  const idToUse = isHostModule ? image.photo_id : image.id;
  const imgAlt = isHostModule ? '' : image.comment || `${image.post_type} #${i}`;

  return (
    <Button
      variant="transparent"
      className="photo-gallery__photo-button"
      data-qa-id="photo-gallery-photo-button"
      onClick={viewPhoto(idToUse)}
      title="Click to display photo"
    >
      <img
        className="photo-gallery__photo"
        data-qa-id="photo-tile"
        src={getThumbnailUrl(url)}
        alt={imgAlt}
        onLoad={() => setIsLoading(false)}
      />
      {isLoading && (
        <Skeleton
          containerClassName="photo-gallery__skeleton-wrapper"
          className="photo-gallery__skeleton"
        />
      )}
    </Button>
  );
};

export const PhotoGallery = ({
  isOpen,
  onClose,
  isHostModule = false,
  galleryName = null,
  onEditGallery,
  handlePost,
  collection = 'photos',
  setPhotoGalleryOpen,
  onViewPhotoFn,
  focusTrapPaused,
  showComingSoonState = false,
}) => {
  const isMediumAndUp = useIsMediumAndUp();
  const isLargishAndUp = useIsLargishAndUp();
  const {getThumbnailUrl} = useImageProxySizer();
  const listRef = useRef(null);
  const fileUploadRef = useRef(null);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const isPremium = useSelector(selectIsPremium);
  const shouldShowAds = !isHostModule && !isPremium;

  const {photos, isLoading} = usePhotos({
    eventId,
    isHostModule,
    includeCount: true,
    perPage: -1,
  });

  const headerActions = useMemo(
    () =>
      isHostModule
        ? <Button
            variant="secondary"
            data-qa-id="edit-photos-btn"
            onClick={onEditGallery}
            icon={<EditLineIcon ratio={0.5} />}
          >
            Edit
          </Button>
        : <>
            <PhotoUploadButton
              variant="secondary"
              handlePost={handlePost}
              fileUploadRef={fileUploadRef}
              icon={<PlusIcon ratio={0.5} />}
            >
              Add
            </PhotoUploadButton>
            <PhotoGalleryUploadInput
              fileUploadRef={fileUploadRef}
              collection={collection}
              setPhotoGalleryOpen={setPhotoGalleryOpen}
              onEditGallery={onEditGallery}
            />
          </>,
    [isHostModule, collection, onEditGallery, setPhotoGalleryOpen, handlePost]
  );

  const bottomComponent = useMemo(() => {
    return (
      shouldShowAds && (
        <div className="photo-gallery__bottom-ad-banner">
          <GoogleAdBanner
            index="1"
            configKey="photoShareGallery"
            pageType="photoshare"
            location={isLargishAndUp ? 'mid' : 'bot'}
          />
        </div>
      )
    );
  }, [isLargishAndUp, shouldShowAds]);

  return (
    <AdaptiveModal
      primaryQaId="photo-gallery"
      id="photo-gallery-modal"
      headerText={galleryName ?? 'Shared album'}
      headerActions={(isHost() || !isHostModule) && photos.length ? headerActions : null}
      headerTextVariant="header5"
      onClose={onClose}
      isOpen={isOpen}
      headerClassName={cx('photo-gallery__header', {
        'photo-gallery__header--justify-end': isHostModule && !galleryName,
      })}
      focusTrapPaused={focusTrapPaused}
      bottomComponent={bottomComponent}
    >
      <div
        className={cx('photo-gallery__list', {
          'photo-gallery__list-host': isHostModule,
          'photo-gallery__list__not-empty': isLoading || photos.length > 0,
          'photo-gallery__coming-soon': showComingSoonState,
        })}
        ref={listRef}
        data-qa-id="photo-gallery-list"
      >
        {!isLoading && photos.length === 0 && (
          <EmptyState
            handlePost={handlePost}
            fileUploadRef={fileUploadRef}
            showComingSoonState={showComingSoonState}
          />
        )}
        {isLoading
          ? // biome-ignore lint/style/noNestedTernary: this ternary is simple enough
            loadingSkeletons(isLargishAndUp ? 9 : isMediumAndUp ? 6 : 4)
          : photos.map((image, i) => {
              const url = isHostModule ? image.url : image.content.url;
              return (
                <PhotoGalleryTile
                  key={url}
                  i={i}
                  image={image}
                  url={url}
                  viewPhoto={onViewPhotoFn}
                  getThumbnailUrl={getThumbnailUrl}
                  isHostModule={isHostModule}
                />
              );
            })}
      </div>
    </AdaptiveModal>
  );
};
